import { OrdersResponseModel } from "../pages/shared/models/api/orders-response.model";
import { AdditionalServiceModel } from "../pages/shared/models/order/additional-service.model";
import fileService from "./file-service";
import { WarehouseModel } from "../pages/shared/models/order/warehouse.model";
import { CountryModel } from "../pages/shared/models/countryModel";
import {
  newOrderSetName,
  newOrderSetProducts,
  newOrderSetRecipient,
  newOrderSetShippingMethod,
  newOrderSetWarehouse,
  newOrderUpdateMultiple,
} from "../redux/slices/new-order-slice";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { ShippingMethodModel } from "../pages/shared/models/order/ShippingMethod.model";
import { Recipient } from "../pages/shared/models/user/recipient/recipient";
import { Product } from "../pages/shared/models/order/product.model";
import { OrderModel } from "../pages/shared/models/order/order-item.model";
import { FileModel } from "../pages/shared/models/FileModel";
import { ConditionEnum } from "../pages/orders/enums/order/condition.enum";
import {
  CurrencyModel,
  WeightUnitModel,
} from "../redux/reducers/static-data.reducers";
import { OrdersCountResponseModel } from "../pages/shared/models/api/orders-count-response.model";
import { CustomersResponseModel } from "../management-pages/shared/model/customers-response.model";
import { LocationResponseModel } from "../management-pages/orders/location-table/model/location-response.model";
import axiosInstance from "../router/interceptor";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4001/bls";

export function onWarehouseChange(
  warehouse: WarehouseModel,
  country: CountryModel,
  currencies: CurrencyModel[],
  dispatch: ThunkDispatch<any, any, any>,
) {
  dispatch(newOrderSetWarehouse({ warehouse, country, currencies }));
}

export function onShippingMethodChange(
  shippingMethod: ShippingMethodModel,
  dispatch: ThunkDispatch<any, any, any>,
) {
  dispatch(newOrderSetShippingMethod(shippingMethod));
}

export function onOrderNameChange(
  name: string,
  dispatch: ThunkDispatch<any, any, any>,
) {
  dispatch(newOrderSetName(name));
}

export function onRecipientChange(
  recipient: Recipient,
  dispatch: ThunkDispatch<any, any, any>,
) {
  dispatch(newOrderSetRecipient(recipient));
}

export function onProductsChange(
  products: Product[],
  dispatch: ThunkDispatch<any, any, any>,
) {
  dispatch(newOrderSetProducts(products));
}

export function onMultipleChange(
  updateData: { [key: string]: any },
  dispatch: ThunkDispatch<any, any, any>,
) {
  dispatch(newOrderUpdateMultiple(updateData));
}

export function getEmptyProduct(
  currencies: CurrencyModel[],
  weightUnits: WeightUnitModel[],
): Product {
  return {
    trackingNumber: "",
    otp: "",
    name: "",
    description: "",
    price: 0,
    currency: currencies[2],
    weight: 0,
    weightUnit: weightUnits[0],
    storeName: "",
    condition: ConditionEnum.NEW,
    invoice: [] as File[],
    image: [] as File[],
  } as Product;
}

export function validateProduct(product: Product): { [key: string]: string } {
  const validations: { [key: string]: string } = {};

  if (!product.trackingNumber) {
    validations["tracking_number"] = "required";
  }
  if (!product.name) {
    validations["product_name"] = "required";
  }
  if (!product.price) {
    validations["price"] = "required";
  }
  if (!product.currency) {
    validations["currency"] = "required";
  }
  // if (!product.category) {
  //   validations["category"] = "required";
  // }
  if (!product.store) {
    validations["store"] = "required";
  }
  if (product.store?.isOther && !product.storeName) {
    validations["storeName"] = "required";
  }

  return validations;
}

export function validateOrder(order: OrderModel): { [key: string]: string } {
  const orderValidations: { [key: string]: string } = {};

  if (order) {
    if (!order.name) {
      orderValidations["order_name"] = "required";
    }
    if (!order.recipient) {
      orderValidations["recipient"] = "required";
    }
  }
  return orderValidations;
}

export function validateProducts(order: OrderModel): {
  [key: string]: { [key: string]: string };
} {
  const result: { [key: string]: { [key: string]: string } } = {};
  let hasValidationErrors = false;

  order &&
    order.products.forEach((product, index) => {
      const productValidations = validateProduct(product);

      if (Object.keys(productValidations).length) {
        result[index] = productValidations;

        hasValidationErrors = true;
      }
    });
  return result;
}

function uploadFilesAndAddToProducts(
  order: OrderModel,
  invoices: Map<string, (File | FileModel)[]>,
  images: Map<string, (File | FileModel)[]>,
) {
  return Promise.allSettled(
    order.products.map((product) => {
      const productInvoices = invoices.get(product.trackingNumber) || [];
      const productNewInvoices = productInvoices.filter(
        (file: File | FileModel) => file instanceof File,
      );

      const invoiceUploads$ = Promise.allSettled(
        productNewInvoices.map((file: File | FileModel) =>
          uploadFile(file).then((newFileData) => newFileData.fileId),
        ),
      );

      const productImages = images.get(product.trackingNumber) || [];
      const productNewImages = productImages.filter(
        (file: File | FileModel) => file instanceof File,
      );

      const imageUploads$ = Promise.allSettled(
        productNewImages.map((file: File | FileModel) =>
          uploadFile(file).then((newFileData) => newFileData.fileId),
        ),
      );

      return Promise.allSettled([invoiceUploads$, imageUploads$]).then(
        ([invoiceUploads, imageUploads]) => {
          if (invoiceUploads.status === "fulfilled") {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            product.invoice = productInvoices
              .map((file) => {
                if (file instanceof File) {
                  const uploadedFile =
                    invoiceUploads.value[
                      productNewInvoices.findIndex((val) => val === file)
                    ];
                  return uploadedFile.status === "fulfilled"
                    ? uploadedFile.value
                    : null;
                } else {
                  return file.fileId;
                }
              })
              .filter((val) => !!val);
          }

          if (imageUploads.status === "fulfilled") {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            product.image = productImages
              .map((file) => {
                if (file instanceof File) {
                  const uploadedFile =
                    imageUploads.value[
                      productNewImages.findIndex((val) => val === file)
                    ];
                  return uploadedFile.status === "fulfilled"
                    ? uploadedFile.value
                    : null;
                } else {
                  return file.fileId;
                }
              })
              .filter((val) => !!val);
          }
        },
      );
    }),
  ).then((result) => {
    return result.every((promise) => promise.status === "fulfilled");
  });
}

async function createOrder(newOrder: OrderModel, language: number) {
  const orderClone: { [key: string]: any } = JSON.parse(
    JSON.stringify(newOrder),
  );

  orderClone["shippingInfo"]["country"] =
    newOrder.shippingInfo.country.countryId;
  orderClone["shippingInfo"]["warehouse"] =
    newOrder.shippingInfo.warehouse.warehouseId;
  orderClone["shippingInfo"]["destinationWarehouse"] =
    newOrder.shippingInfo.destinationWarehouse?.warehouseId;
  orderClone["shippingInfo"]["homeDeliveryAddress"] =
    newOrder.shippingInfo.homeDeliveryAddress?.addressId;
  orderClone["shippingInfo"]["shippingMethod"] =
    newOrder.shippingInfo.shippingMethod.shippingMethodId;
  orderClone["recipient"] = newOrder.recipient?.recipientId;
  orderClone["weightUnit"] = newOrder.weightUnit?.weightUnitId;

  const invoices = new Map<string, (File | FileModel)[]>();
  const images = new Map<string, (File | FileModel)[]>();

  orderClone["products"].forEach(
    (product: { [key: string]: any }, index: number) => {
      product["currency"] = newOrder.products[index].currency.currencyId;
      product["weightUnit"] = newOrder.products[index].weightUnit?.weightUnitId;

      if (product["category"]) {
        product["category"] = product["category"]["productCategoryId"];
      }

      if (product["store"]) {
        product["store"] = product["store"]["storeId"];
      }

      if (product["storeName"]) {
        product["storeName"] =
          !product["store"]["isOther"] && product["storeName"];
      }

      if (product["invoice"].length) {
        invoices.set(
          product["trackingNumber"],
          newOrder.products[index].invoice,
        );
      }
      if (product["image"].length) {
        images.set(product["trackingNumber"], newOrder.products[index].image);
      }

      delete product["invoice"];
      delete product["image"];
    },
  );

  orderClone["additionalService"] = orderClone["additionalService"].map(
    (additionalService: { [key: string]: any }) =>
      (additionalService["additionalServiceId"] =
        additionalService.additionalServiceId),
  );

  try {
    const authorizationHeader = configAuthorizationHeader();
    if (invoices.size || images.size) {
      return uploadFilesAndAddToProducts(
        orderClone as OrderModel,
        invoices,
        images,
      )
        .then((succeeded) => {
          if (succeeded) {
            return axiosInstance.post(
              `${API_URL}/orders/create`,
              {
                ...orderClone,
                language,
              },
              authorizationHeader,
            );
          }
        })
        .then(async (response) => {
          const orderId: number = response?.data?.id;
          if (orderId) {
            return Promise.resolve(orderId);
          }
          return Promise.resolve(null);
        });
    } else {
      return axiosInstance
        .post(
          `${API_URL}/orders/create`,
          { ...orderClone, language },
          authorizationHeader,
        )
        .then(async (response) => {
          const orderId: number = response?.data?.id;
          if (orderId) {
            return Promise.resolve(orderId);
          }
          return Promise.resolve(null);
        })
        .catch((reason) => {
          return reason.response;
        });
    }
  } catch (error) {
    console.error(error);
  }
}

async function createUnannouncedOrder(updateData: object, language: number) {
  try {
    const authorizationHeader = configAuthorizationHeader();
    return axiosInstance
      .post(
        `${API_URL}/order/create-unannounced-order`,
        {
          ...updateData,
          language,
        },
        authorizationHeader,
      )
      .then(() => {
        return Promise.resolve(null);
      })
      .catch((error) => {
        // Check if it's a duplicate tracking number error
        if (error.response?.status === 500 && error.response?.data?.includes('trackingNumber_1 dup key')) {
          return Promise.reject({
            reason: `already_exists_in_warehouse`,
          });
        }
        // For other errors, log and return generic error
        console.error('error', error);
      });
  } catch (error) {
    console.error('error', error);
    return Promise.reject({
      reason: error,
      type: 'NETWORK_ERROR',
      message: 'Network error occurred. Please check your connection and try again.',
      originalError: error
    });
  }
}

async function getMyOrders(
  page?: number,
  filter: object = {},
  limit?: number,
): Promise<OrdersResponseModel> {
  try {
    const authorizationHeader = configAuthorizationHeader();

    const queryParams: { [key: string]: any } = {};

    if (page) {
      queryParams["page"] = page;
    }
    if (limit) {
      queryParams["limit"] = limit;
    }

    const sorting = { enteredDate: -1 };

    return axiosInstance
      .post(
        `${API_URL}/orders/my`,
        {
          filter: filter,
          sorting: sorting,
        },
        { params: { ...queryParams }, ...authorizationHeader },
      )
      .then((response) => {
        return response?.data as OrdersResponseModel;
      });
  } catch (error) {
    console.error(error);

    return {
      currentPage: 0,
      orders: [],
      pageSize: 0,
      totalItems: 0,
    };
  }
}

async function getOrderByGivenFilterCriteria(
  filter: object = {},
): Promise<OrderModel | undefined> {
  try {
    const authorizationHeader = configAuthorizationHeader();

    return axiosInstance
      .post(
        `${API_URL}/order/get-by-filter`,
        { filter: filter },
        authorizationHeader,
      )
      .then((response) => {
        return response?.data?.order as OrderModel;
      });
  } catch (error) {
    console.error(error);
  }
}

async function getOrdersCount(
  filter: object = {},
): Promise<OrdersCountResponseModel> {
  try {
    const authorizationHeader = configAuthorizationHeader();

    return axiosInstance
      .post(`${API_URL}/orders/count`, { filter: filter }, authorizationHeader)
      .then((response) => {
        return response?.data as OrdersCountResponseModel;
      });
  } catch (error) {
    console.error(error);

    return {
      totalItems: 0,
    };
  }
}

async function getAllOrders(
  filter: object = {},
  limit?: number,
  page?: number,
): Promise<OrdersResponseModel> {
  try {
    const authorizationHeader = configAuthorizationHeader();

    const params = new URLSearchParams();

    if (limit) {
      params.append("limit", "" + limit);
    }
    params.append("page", "" + (page || 0));

    const sorting = { enteredDate: -1 };

    return axiosInstance
      .post(
        `${API_URL}/orders`,
        { sorting, filter },
        { params, ...authorizationHeader },
      )
      .then((response) => {
        return response?.data as OrdersResponseModel;
      });
  } catch (error) {
    console.error(error);

    return {
      currentPage: 0,
      orders: [],
      pageSize: 0,
      totalItems: 0,
    };
  }
}

async function getOrderById(orderId: number) {
  try {
    const authorizationHeader = configAuthorizationHeader();
    return axiosInstance
      .get(`${API_URL}/orders/${orderId}`, authorizationHeader)
      .then((response) => {
        return response?.data as OrderModel;
      });
  } catch (error) {
    console.error(error);
  }
}

async function collectOrdersGroupedByUser(
  filter: object = {},
  limit?: number,
  page?: number,
) {
  try {
    const authorizationHeader = configAuthorizationHeader();

    const params = new URLSearchParams();

    if (limit) {
      params.append("limit", "" + limit);
    }
    params.append("page", "" + (page || 0));

    return axiosInstance
      .post(`${API_URL}/order/collect-orders-grouped-by-user`, filter, {
        params,
        ...authorizationHeader,
      })
      .then((response) => {
        return response?.data as CustomersResponseModel;
      });
  } catch (error) {
    console.error(error);

    return {
      currentPage: 0,
      data: [],
      pageSize: 0,
      totalItems: 0,
    };
  }
}

async function getCombinedByLocation(
  filter: object = {},
  limit?: number,
  page?: number,
) {
  try {
    const authorizationHeader = configAuthorizationHeader();

    const params = new URLSearchParams();

    if (limit) {
      params.append("limit", "" + limit);
    }
    params.append("page", "" + (page || 0));

    return axiosInstance
      .post(
        `${API_URL}/orders/get-combined-by-location`,
        { filter },
        { params, ...authorizationHeader },
      )
      .then((response) => {
        return response?.data as LocationResponseModel;
      });
  } catch (error) {
    console.error(error);

    return {
      data: [],
      page: 0,
      pageSize: 10,
      totalGroups: 0,
      totalPages: 0,
    };
  }
}

async function deleteOrderById(orderId: number) {
  try {
    const authorizationHeader = configAuthorizationHeader();
    return axiosInstance.delete(
      `${API_URL}/orders/${orderId}`,
      authorizationHeader,
    );
  } catch (error) {
    console.error(error);
  }
}

async function updateById(
  updateData: {
    [key: string]: any;
  },
  orderId: number,
  language: number,
): Promise<OrderUpdateMeta> {
  try {
    const authorizationHeader = configAuthorizationHeader();
    const updateDataClone: { [key: string]: any } = JSON.parse(
      JSON.stringify(updateData),
    );

    if (updateData["shippingInfo"]) {
      updateDataClone["shippingInfo"]["country"] =
        updateData.shippingInfo.country.countryId;
      updateDataClone["shippingInfo"]["warehouse"] =
        updateData.shippingInfo.warehouse.warehouseId;
      updateDataClone["shippingInfo"]["destinationWarehouse"] =
        updateData.shippingInfo.destinationWarehouse?.warehouseId || null;
      updateDataClone["shippingInfo"]["homeDeliveryAddress"] =
        updateData.shippingInfo.homeDeliveryAddress?.addressId || null;
      updateDataClone["shippingInfo"]["shippingMethod"] =
        updateData.shippingInfo.shippingMethod.shippingMethodId;
    }
    if (updateData["recipient"]) {
      updateDataClone["recipient"] = updateData.recipient?.recipientId;
    }
    if (updateData["additionalService"]) {
      updateDataClone["additionalService"] = updateData[
        "additionalService"
      ].map((service: AdditionalServiceModel) => service.additionalServiceId);
    }
    if (updateData["weightUnit"]) {
      updateDataClone["weightUnit"] = updateData["weightUnit"].weightUnitId;
    }
    if (updateData["isLabelPrinted"]) {
      updateDataClone["isLabelPrinted"] = updateData["isLabelPrinted"];
    }

    const invoices = new Map<string, (File | FileModel)[]>();
    const images = new Map<string, (File | FileModel)[]>();

    if (updateData["products"]) {
      updateDataClone["products"].forEach(
        (product: { [key: string]: any }, index: number) => {
          product["currency"] = updateData.products[index].currency.currencyId;
          product["weightUnit"] =
            updateData.products[index].weightUnit?.weightUnitId;

          if (product["type"]) {
            product["type"] = updateData.products[index].type.productTypeId;
          }
          if (product["category"]) {
            product["category"] = product["category"]["productCategoryId"];
          }

          if (product["storeName"]) {
            product["storeName"] = product["store"]["isOther"]
              ? product["storeName"]
              : "";
          }

          if (product["store"]) {
            if (!product["store"]["isOther"]) {
              product["storeName"] = product["storeName"][0];
            }
            product["store"] = product["store"]["storeId"];
          }

          if (product["invoice"].length) {
            invoices.set(
              product["trackingNumber"],
              updateData.products[index]["invoice"],
            );
          }
          if (product["image"].length) {
            images.set(
              product["trackingNumber"],
              updateData.products[index]["image"],
            );
          }

          delete product["invoice"];
          delete product["image"];
        },
      );
    }

    if (invoices.size || images.size) {
      return uploadFilesAndAddToProducts(
        updateDataClone as OrderModel,
        invoices,
        images,
      )
        .then((succeeded) => {
          if (succeeded) {
            return axiosInstance.put(
              `${API_URL}/orders/${orderId}`,
              {
                ...updateDataClone,
                language,
              },
              authorizationHeader,
            );
          }
        })
        .then((response) => response?.data as OrderUpdateMeta);
    } else {
      return axiosInstance
        .put(
          `${API_URL}/orders/${orderId}`,
          {
            ...updateDataClone,
            language,
          },
          authorizationHeader,
        )
        .then((response) => response?.data as OrderUpdateMeta);
    }
  } catch (error) {
    console.error(error);

    return {
      acknowledged: false,
      matchedCount: 0,
      modifiedCount: 0,
      upsertedCount: 0,
      upsertedId: 0,
    };
  }
}

async function updateStateById(updateData: {
  [key: string]: any;
}): Promise<OrderUpdateMeta> {
  try {
    const authorizationHeader = configAuthorizationHeader();
    const orderId = updateData["orderId"];
    if (updateData["orderId"]) {
      delete updateData["orderId"];
    }
    return axiosInstance
      .put(
        `${API_URL}/orders/state/${orderId}`,
        updateData,
        authorizationHeader,
      )
      .then((response) => response?.data as OrderUpdateMeta);
  } catch (error) {
    console.error(error);

    return {
      acknowledged: false,
      matchedCount: 0,
      modifiedCount: 0,
      upsertedCount: 0,
      upsertedId: 0,
    };
  }
}

async function bulkUpdate(
  orderIds: number[],
  data: object,
  language: number,
): Promise<OrderUpdateMeta> {
  try {
    const authorizationHeader = configAuthorizationHeader();

    const requestBody = {
      orders: orderIds,
      data,
    };

    return axiosInstance
      .put(
        `${API_URL}/orders/bulk-update`,
        { ...requestBody, language },
        authorizationHeader,
      )
      .then((response) => response?.data as OrderUpdateMeta);
  } catch (error) {
    console.error(error);

    return {
      acknowledged: false,
      matchedCount: 0,
      modifiedCount: 0,
      upsertedCount: 0,
      upsertedId: 0,
    };
  }
}

async function getMyOrdersStatesSummary() {
  try {
    const authorizationHeader = configAuthorizationHeader();
    return axiosInstance.get(
      `${API_URL}/orders/states/summary`,
      authorizationHeader,
    );
  } catch (error) {
    console.error(error);
  }
}

async function getTotalPaymentData(filter: object) {
  try {
    const authorizationHeader = configAuthorizationHeader();

    return axiosInstance
      .post(`${API_URL}/orders/payment-data`, { filter }, authorizationHeader)
      .then((response) => {
        return response?.data as any;
      });
  } catch (error) {
    console.error(error);
  }
}

async function getTotalOrdersDataGroupedByCountries(
  filter: object,
  withStates?: boolean,
) {
  try {
    const authorizationHeader = configAuthorizationHeader();

    return axiosInstance
      .post(
        withStates
          ? `${API_URL}/orders/grouped-by-countries/states`
          : `${API_URL}/orders/grouped-by-countries`,
        { filter },
        authorizationHeader,
      )
      .then((response) => {
        return response?.data as any;
      });
  } catch (error) {
    console.error('error:', error);
  }
}

async function getOldestDateByState(stateId: number) {
  try {
    const authorizationHeader = configAuthorizationHeader();
    return axiosInstance.get(
      `${API_URL}/orders/oldest/by-state/${stateId}`,
      authorizationHeader,
    );
  } catch (error) {
    console.error(error);
  }
}

function configAuthorizationHeader() {
  let token = localStorage.getItem("access_token");
  const refreshToken = localStorage.getItem("refresh_token");
  token = token ? token.replaceAll('"', "") : "";
  return {
    headers: {
      Authorization: "Bearer ".concat(token),
      "x-refresh-token": refreshToken,
    },
  };
}

function uploadFile(fileInfo: File | FileModel): Promise<FileModel> {
  const isNewFile = fileInfo instanceof File;

  if (isNewFile) {
    return fileInfo
      ?.stream()
      .getReader()
      .read()
      .then((fileData) => {
        if (fileData.value) {
          const fileName = fileInfo?.name || "Document";

          const blob = new Blob([fileData.value], { type: fileInfo?.type });
          return new File([blob], fileName, { type: fileInfo?.type });
        } else {
          return null;
        }
      })
      .then((fileBlob) => {
        if (fileBlob) {
          const formData = new FormData();
          formData.append("file", fileBlob);

          return fileService.uploadFile(formData);
        }
      })
      .then((response) => {
        return response?.data;
      });
  }
  return Promise.resolve(fileInfo);
}

async function updateByTrackingNumber(
  updateData: object,
  languageId: number,
): Promise<OrderUpdateMeta> {
  const authorizationHeader = configAuthorizationHeader();

  const data = {
    updateData,
    languageId,
  };

  return axiosInstance
    .put(`${API_URL}/order/by-tracking-number`, data, authorizationHeader)
    .then((response) => response?.data as OrderUpdateMeta);
}

async function announceOrder(
  orderId: number,
  updateData: object,
): Promise<OrderUpdateMeta> {
  const authorizationHeader = configAuthorizationHeader();

  return axiosInstance
    .put(
      `${API_URL}/order/announce-order/${orderId}`,
      updateData,
      authorizationHeader,
    )
    .then((response) => response?.data as OrderUpdateMeta);
}

async function updateState(orderId: number, orderUpdateData: any) {

  try {
    const authorizationHeader = configAuthorizationHeader();
    return axiosInstance
      .put(`${API_URL}/barekampost/admin/orders/${orderId}`, orderUpdateData, authorizationHeader)
      .then((response) => response?.data);
  } catch (error) {
    console.error(error);
    return {
      acknowledged: false,
      matchedCount: 0,
      modifiedCount: 0,
      upsertedCount: 0,
      upsertedId: 0,
    };
  }
}

export default {
  createOrder,
  getMyOrders,
  getAllOrders,
  getOrderById,
  collectOrdersGroupedByUser,
  getOldestDateByState,
  getMyOrdersStatesSummary,
  getTotalPaymentData,
  getTotalOrdersDataGroupedByCountries,
  updateById,
  updateStateById,
  deleteOrderById,
  updateByTrackingNumber,
  getOrdersCount,
  getOrderByGivenFilterCriteria,
  getCombinedByLocation,
  createUnannouncedOrder,
  bulkUpdate,
  announceOrder,
  updateState
};

export interface OrderUpdateMeta {
  acknowledged: boolean;
  matchedCount: number;
  modifiedCount: number;
  upsertedCount: number;
  upsertedId: number;
}
